import React, { createContext, useMemo, useState } from "react";
import { useRouteMatch } from "react-router-dom";

import { noop } from "lodash";
import { graphql } from "relay-runtime";

import PaidFeatureBlock from "~/billing/PaidFeatureBlock";
import { EmptyState } from "~/common";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { Source } from "~/okrs/components/Filters";
import ObjectiveList from "~/okrs/ObjectiveList";
import { ReactComponent as NotFoundIllo } from "~/streamsPage/404-illustration.svg";
import TeamHeader from "~/team/components/TeamHeader";

import type { ObjectiveStatus } from "~/__generated__/ObjectiveQueryViewer.graphql";
import type { teamQuery as TeamQuery } from "~/__generated__/teamQuery.graphql";
import type { teamQuery_Info$data as TeamData } from "~/__generated__/teamQuery_Info.graphql";

import stylesStream from "~/stream/css/Stream.module.css";

const teamQuery = graphql`
	query teamQuery($id: ID!) {
		viewer {
			account {
				billingAccount {
					hasAccessToTeams: hasAccessToFeature(feature: teams)
				}
				integrations {
					gsuite {
						id
					}
					office365 {
						id
					}
				}
			}
			team: node(id: $id) {
				... on Team {
					...teamQuery_Info @relay(mask: false)
					objectivesFilterAssignee
					objectivesFilterStatus
					objectivesFilterCycle
				}
			}
		}
	}
`;

graphql`
	fragment teamQuery_Info on Team {
		id
		title
		description
		members {
			id
			fullName
			email
			image
			isActive
			isConfigured
			hasReceivedInvites
			isTopUser
		}
		membersCount
		parent {
			id
			title
		}
		sync {
			fromManager {
				id
				fullName
			}
			fromExternalGroupId
			fromExternalGroupName
			syncFailed
		}
		handle
		location
		archived
		hasAccess
		isAccountWideTeam
	}
`;

export type TeamContextType = {
	team?: Omit<TeamData, " $fragmentType">;
	refetchId: number;
	setRefetchId: React.Dispatch<React.SetStateAction<number>>;
};

export const TeamContext = createContext<TeamContextType>({
	team: undefined,
	refetchId: 0,
	setRefetchId: noop,
});

export default function Team() {
	const match = useRouteMatch<{ id: string }>();
	const [refetchId, setRefetchId] = useState<number>(0);
	const { id } = match.params;

	const props = useLazyLoadQuery<TeamQuery>(teamQuery, { id }, { fetchPolicy: "store-or-network" });

	// TODO: Fix me
	// @ts-ignore
	const contextData = useMemo<TeamContextType>(() => {
		return {
			team: props.viewer.team,
			refetchId,
			setRefetchId,
		};
	}, [props, refetchId, setRefetchId]);

	if (!props.viewer.account.billingAccount.hasAccessToTeams) {
		return <PaidFeatureBlock id={"teams"} />;
	}

	if (!props.viewer.team) {
		return <EmptyState illustration={NotFoundIllo} title="Uh oh, we couldn't find this item." />;
	}

	return (
		<div className={stylesStream.container}>
			<TeamContext.Provider value={contextData}>
				<TeamHeader
					/* TODO: Fix me */
					/* @ts-ignore */
					team={props.viewer.team}
					isGoogleWorkspace={!!props.viewer.account.integrations?.gsuite}
					isOffice365Workspace={!!props.viewer.account.integrations?.office365}
				/>
				<ObjectiveList
					fromSource={props.viewer.team.id}
					source={Source.TEAM}
					presetFilters={{
						// TODO: Fix me
						// @ts-ignore
						assigneeIds: props.viewer.team.objectivesFilterAssignee || undefined,
						status: props.viewer.team.objectivesFilterStatus as ObjectiveStatus,
						// @ts-ignore
						cycles: props.viewer.team.objectivesFilterCycle || undefined,
					}}
				/>
			</TeamContext.Provider>
		</div>
	);
}
